import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import endpoints from '../../../../../../api/endpoints';
import { useFetch } from '../../../../../../api/useFetch';
import CommonReports from '../../../../../common/reports';
import { downloadReport } from '../../../../../common/reports/utils/downloadReport';
import { HandleDownloadReport } from '../../../../../common/reports/actions/interfaces';
import { getPermissionToGenerateReports } from '../../../../../common/reports/utils/mapReportToTableStructure';
import { addParams, fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';
import { FiltersFormValues, FiltersInitialData, ReportsData } from '../../../../../common/reports/interfaces';

import locales from './locales';
import FiltersForm from './filters/filtersForm';
import FilterDetails from './filters/filterDetails';
import FiltersSummary from './filters/filtersSummary';
import { mapTableDataIntoApiFormat } from './dataTransformator';
import { filtersValidationSchema } from './filters/filtersValidation';
import { MerchantPaymentReportCreateForm, MerchantPaymentReportDetailsMetadataResponse } from './interfaces';

const initFilters = {
  name: '',
  statuses: [],
  operationIds: [],
  merchantCodes: [],
  from: null,
  to: null,
  customerMsisdns: []
};

const Reports = () => {
  const { data = {} as ReportsData, loading, refresh, error } = useFetch<ReportsData>({
    pathname: endpoints.merchantPaymentReports
  });

  const handleDeleteReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.merchantPaymentReports}/${id}`, {
      method: 'delete'
    });
  const handleCancelReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.merchantPaymentReports}/${id}`, {
      method: 'delete'
    });

  const handleRetryReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.merchantPaymentReportsRetry(id)}`, {
      method: 'POST'
    });

  const handleRegenerateReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.merchantPaymentReportsRegenerate(id)}`, {
      method: 'POST'
    });

  const handleGenerateReportRequest = (values: MerchantPaymentReportCreateForm) =>
    fetchWithTokenRefresh(endpoints.merchantPaymentReports, addParams('POST', mapTableDataIntoApiFormat(values)));

  const handleDownloadReport: HandleDownloadReport = (id, name, size) => {
    downloadReport(id, `${name}.csv`, 'merchantPayment', size);
  };

  const location = useLocation<{ filters: FiltersInitialData } | undefined>();

  const [initialValues, setInitialValues] = useState(location.state?.filters || initFilters);

  return (
    <CommonReports
      error={error}
      filters={{
        form: (filtersFormValues: FiltersFormValues<MerchantPaymentReportCreateForm>) => (
          <FiltersForm {...filtersFormValues} />
        ),
        validation: filtersValidationSchema,
        details: (metadata: MerchantPaymentReportDetailsMetadataResponse) => <FilterDetails filters={metadata} />,
        summary: (metadata: MerchantPaymentReportDetailsMetadataResponse) => <FiltersSummary filters={metadata} />,
        initialValues,
        clearValues: () => {
          setInitialValues(initFilters);
        },
        predefinedReportGeneration: Boolean(location.state?.filters)
      }}
      locales={locales}
      data={data}
      emptyMsg="You don't have any Merchant Payment reports to display"
      loading={loading}
      refresh={refresh}
      handleDeleteReportRequest={handleDeleteReportRequest}
      handleCancelReportRequest={handleCancelReportRequest}
      handleRetryReportRequest={handleRetryReportRequest}
      handleRegenerateReportRequest={handleRegenerateReportRequest}
      handleGenerateReportRequest={handleGenerateReportRequest}
      handleDownloadReport={handleDownloadReport}
      generationAllowed={getPermissionToGenerateReports(data.limits?.status)}
    />
  );
};

export default Reports;
